import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../App";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Ticket,
  Loader2,
  Store,
  Calendar,
  CheckCircle2,
  XCircle,
  Sparkles,
  ArrowLeft,
} from "lucide-react";

function statusBadge(status) {
  if (status === "redeemed")
    return (
      <Badge className="bg-emerald-600 text-white border-0 gap-1">
        <CheckCircle2 className="w-3 h-3" />
        Canjeado
      </Badge>
    );
  if (status === "expired")
    return (
      <Badge className="bg-neutral-500 text-white border-0 gap-1">
        <XCircle className="w-3 h-3" />
        Expirado
      </Badge>
    );
  return (
    <Badge className="bg-amber-500 text-white border-0 gap-1">
      <Sparkles className="w-3 h-3" />
      Listo para canjear
    </Badge>
  );
}

function CouponCard({ coupon }) {
  const [showQR, setShowQR] = useState(false);
  const isActive = coupon.status === "claimed";
  const expires = coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString("es-MX") : null;

  return (
    <div
      className="card-base p-0 overflow-hidden"
      data-testid={`coupon-card-${coupon.coupon_id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
        {coupon.business_logo ? (
          <img
            src={coupon.business_logo}
            alt=""
            className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-neutral-200 flex items-center justify-center flex-shrink-0">
            <Store className="w-6 h-6 text-neutral-500" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-lg text-agave truncate">{coupon.business_name}</h3>
          <p className="text-xs text-neutral-600">{coupon.business_city}</p>
        </div>
        {coupon.discount_percent ? (
          <div className="bg-terracotta text-white text-lg font-bold px-3 py-1 rounded-full">
            -{Math.round(coupon.discount_percent)}%
          </div>
        ) : null}
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {coupon.discount_details && (
          <p className="text-sm text-neutral-700">{coupon.discount_details}</p>
        )}
        <div className="flex items-center justify-between flex-wrap gap-2">
          {statusBadge(coupon.status)}
          {expires && isActive && (
            <span className="text-xs text-neutral-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Válido hasta {expires}
            </span>
          )}
        </div>

        {/* Code */}
        <div className="bg-neutral-900 text-white rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/60">Código</p>
            <p className="text-2xl font-mono font-bold tracking-wider" data-testid={`coupon-code-${coupon.coupon_id}`}>
              {coupon.code}
            </p>
          </div>
          {isActive && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowQR((v) => !v)}
              data-testid={`coupon-toggle-qr-${coupon.coupon_id}`}
            >
              {showQR ? "Ocultar QR" : "Mostrar QR"}
            </Button>
          )}
        </div>

        {/* QR */}
        {showQR && isActive && coupon.qr_token && (
          <div className="bg-white rounded-xl p-4 flex flex-col items-center" data-testid={`coupon-qr-${coupon.coupon_id}`}>
            <QRCodeSVG value={coupon.qr_token} size={180} level="M" includeMargin />
            <p className="text-xs text-neutral-500 mt-2 text-center">
              Muestra este QR (o el código) al negocio para canjear tu descuento.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between text-xs">
          <Link
            to={`/guia/${coupon.business_id}`}
            className="text-terracotta font-medium hover:underline"
            data-testid={`coupon-biz-link-${coupon.coupon_id}`}
          >
            Ver negocio →
          </Link>
          {coupon.discount_terms && (
            <span className="text-neutral-400 italic line-clamp-1 ml-2">
              {coupon.discount_terms}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GuiaCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/guia/coupons/mine");
        if (!cancelled) setCoupons(data.items || []);
      } catch {
        if (!cancelled) setCoupons([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const active = coupons.filter((c) => c.status === "claimed");
  const past = coupons.filter((c) => c.status !== "claimed");

  return (
    <div className="min-h-screen bg-sand pb-12" data-testid="guia-coupons-page">
      <div className="container-main py-6">
        <Link
          to="/guia"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-agave mb-4"
          data-testid="coupons-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la Guía
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center">
            <Ticket className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-agave">Mis cupones</h1>
            <p className="text-sm text-neutral-600">
              Tus descuentos reclamados en Guía RivoNes
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center" data-testid="coupons-loading">
            <Loader2 className="w-8 h-8 animate-spin text-terracotta mx-auto" />
          </div>
        ) : coupons.length === 0 ? (
          <div className="card-base text-center py-16" data-testid="coupons-empty">
            <Ticket className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
            <p className="font-medium text-neutral-700 mb-1">Aún no has reclamado cupones</p>
            <p className="text-sm text-neutral-500 mb-4">
              Explora negocios y reclama tus descuentos exclusivos.
            </p>
            <Link to="/guia" className="btn-primary inline-flex">
              Explorar Guía
            </Link>
          </div>
        ) : (
          <>
            {active.length > 0 && (
              <section className="mb-8">
                <h2 className="text-lg font-heading font-semibold text-agave mb-3">
                  Activos ({active.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {active.map((c) => (
                    <CouponCard key={c.coupon_id} coupon={c} />
                  ))}
                </div>
              </section>
            )}
            {past.length > 0 && (
              <section>
                <h2 className="text-lg font-heading font-semibold text-agave mb-3">
                  Anteriores ({past.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-75">
                  {past.map((c) => (
                    <CouponCard key={c.coupon_id} coupon={c} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
